import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import loadable from '@loadable/component';
import { plainTextStyle } from '@westwing/ui-kit/typography';
import TextWrapperPadding from '../../Generic/BoxHelpers';
import FilterContainer, { UnknownFilter } from '../GenericFiltersFlyout/FilterContainer';
import { FilterValue } from '../GenericFiltersFlyout/filterValueService';
import { Filter, FilterTypes, NewFilterValues } from 'Client/components/Generic/FilterAndSortBox/types';
import { getEnumKey } from 'Common/utils/objectUtils';

const RangeFilters = loadable(() => import('../GenericFiltersFlyout/RangeFilters'));

const FiltersGroupContainer = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.charcoal20};
    padding: 15px 0;

    :first-child {
        border-top: none;
    }
`;

export const StyledMenuItem = styled.div`
    display: grid;
    grid-template-columns: auto 1fr 10px;
    grid-gap: 4px;
`;

const StyledTextSpan = styled.span`
    ${plainTextStyle}
`;

const StyledRestrictedLengthSpan = styled(StyledTextSpan)`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.turquoise100};
`;

const StyledFilterDescription = styled.div`
    color: ${({ theme }) => theme.colors.turquoise100};
    display: grid;
    grid-template-columns: auto auto auto 1fr;
    text-transform: capitalize;
`;

const StyledParentContainer = styled.div`
    padding-top: 10px;
`;

interface FilterGroupProps {
    group: Filter;
    handleValueChange: (value: NewFilterValues) => void;
    filterValue: FilterValue;
    isOpen?: boolean;
}

interface DescriptionProps {
    filterValue: FilterValue;
    group: Filter;
}

const Description: FC<DescriptionProps> = ({ filterValue, group }) => {
    if (!filterValue) {
        return <div />;
    }

    const { valueKeys = [] } = group;

    const filterValues = Array.isArray(filterValue) ? filterValue : [filterValue];
    const filterKeysOrValues = filterValues.map(
        value => getEnumKey({ enumValue: value, enumObject: valueKeys }) || value
    );
    const text = filterKeysOrValues.join(',');

    if (!text) {
        return <div />;
    }
    return (
        <StyledFilterDescription>
            (<StyledRestrictedLengthSpan>{text}</StyledRestrictedLengthSpan>)
        </StyledFilterDescription>
    );
};

export const FilterGroup: FC<FilterGroupProps> = ({ group, handleValueChange, filterValue }) => {
    useEffect(() => {
        RangeFilters.preload();
    });

    const onFilterValueChange = (newFilterValue: NewFilterValues) => {
        handleValueChange(newFilterValue);
    };

    if (!Object.values(FilterTypes).includes(group.type)) {
        return <UnknownFilter filter={group} />;
    }

    return (
        <FiltersGroupContainer data-testid={group.name}>
            <StyledMenuItem>
                <TextWrapperPadding leftPadding={0} rightPadding={10}>
                    <StyledTextSpan>{group.displayName}</StyledTextSpan>
                </TextWrapperPadding>
                <Description filterValue={filterValue} group={group} />
            </StyledMenuItem>
            <StyledParentContainer>
                <FilterContainer filterValue={filterValue} group={group} handleValueChange={onFilterValueChange} />
            </StyledParentContainer>
        </FiltersGroupContainer>
    );
};

export default FilterGroup;
